import React, { useEffect, useRef } from "react";
import { Checkbox, Box } from "@mui/material";
import Text from "components/common/Text";
import Stack from "components/common/Stack";
import SectionTitle from "components/SectionTitle";
import JarvisNote from "components/JarvisNote";
import JarvisButton from "components/JarvisButton";
import RiskProfileMeter from "components/Onboarding/RiskProfileMeter";
import FeatureCard from "components/common/FeatureCard";
import { formatHelper } from "helper/format";
import ShieldIcon from "@mui/icons-material/Shield";
import { useDispatch, useSelector } from "react-redux";
import { axios } from "helper/axios";
import { thirdpartyConstants } from "stores/thirdparty/thirdpartyConstants";
import { masterActions } from "stores/master/masterActions";
import { localStore } from "stores/localStorage";

function Assessment({
  selectedRiskId,
  riskProfiles,
  isDeclarationChecked,
  onDeclarationChange,
  onConfirm,
  onChangeProfile,
  readMore,
  onToggleReadMore,
  isLoading,
  loginProfile,
  productName,
  suitableText,
  productCode,
}) {
  const dispatch = useDispatch();
  const fetchedRiskDataRef = useRef(false);
  const currentRiskProfile = riskProfiles?.find(
    (item) => item.id === selectedRiskId
  );

  const riskData = useSelector((state) => state.master.riskData);

  // Fallback: PRODUCT FEATURES need riskData. Invest Now usually loads it earlier;
  // on Android/iOS Assessment often mounts with an empty store — fetch once if needed.
  useEffect(() => {
    if (riskData?.length || fetchedRiskDataRef.current) {
      return;
    }

    const code = productCode || localStore.getActiveProduct();
    if (!code) {
      return;
    }

    fetchedRiskDataRef.current = true;

    let cancelled = false;
    (async () => {
      try {
        const response = await axios.get(
          `${thirdpartyConstants.PORTFOLIO_SUBSCRIPTION_PLANS_API}?productCode=${code}`,
        );
        if (!cancelled && response?.[0]?.riskData) {
          dispatch(masterActions.getRiskDetails(response[0].riskData));
        }
      } catch (error) {
        console.error("API Error:", error);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [dispatch, productCode, riskData?.length]);

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, background: "#fff" }}>
      <Stack
        direction={{ xs: "column", md: "row" }}
        justifyContent="space-between"
        pb={{ xs: 4, md: 10 }}
        // pt={{ xs: 2, md: 6 }}
        pt={{ xs: 1, md: 1 }}
        gap={4}
      >
        {/* LEFT SECTION */}
        <Stack sx={{ flex: 1, minWidth: 0 }}>
          <SectionTitle heading="Assessment Result" sx={{ mb: 2 }} />

          <Text mb={1} variant="small" sx={{ color: "#676E76" }}>
            Your risk profile is same throughout the product.
          </Text>

          <JarvisNote
            sx={{ backgroundColor: "#FFEEE4" }}
            heading="Please Note:"
            description="Your risk profile will remain the same for all Jarvis products, and it cannot be changed for 6 months."
          />

          {/* RISK METER */}
          <Stack alignItems="center" mt={2}>
            <RiskProfileMeter
              chartWidth={350}
              riskProfiles={riskProfiles}
              selectedRiskId={selectedRiskId}
            />

            {loginProfile && currentRiskProfile && (
              <Stack alignItems="center" mt={2}>
                <Text
                  variant="caption1"
                  sx={{ textTransform: "uppercase", color: "#676E76" }}
                >
                  Congratulations, {loginProfile?.name}!
                </Text>

                <Text
                  fontSize={{ xs: "22px", md: "32px" }}
                  mt={1}
                  fontWeight="600"
                  sx={{ textAlign: "center" }}
                >
                  You are{" "}
                  {formatHelper.checkVowel(currentRiskProfile?.name)
                    ? "an"
                    : "a"}{" "}
                  {currentRiskProfile?.name} Investor
                </Text>
              </Stack>
            )}
          </Stack>

          {/* DECLARATION */}
          <Stack
            direction="row"
            mt={2}
            alignItems="flex-start"
            sx={{ marginTop: 6 }}
          >
            <Checkbox
              checked={isDeclarationChecked}
              onChange={onDeclarationChange}
            />

            <Box>
              <Text
                variant="caption1"
                sx={{
                  color: "#676E76",
                  maxWidth: 480,
                  ...(readMore
                    ? {}
                    : {
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        display: "-webkit-box",
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: "vertical",
                      }),
                }}
              >
                I confirm that the information as mentioned by me in the
                preceding questionnaire is true and correct to the best of my
                knowledge. I hereby agree, declare and confirm that JARVIS is
                relying on the questionnaire for the purpose of Risk Profiling
                (assessment of my risk profile). I hereby agree, declare and
                confirm that the Portfolio of equity stocks / liquid bees / gold
                bees proposed by JARVIS is based on the Risk Profiling done by
                JARVIS. I understand that the risk profile determined based on
                my responses will remain unchanged for the next 6 months and
                will be applicable across all Jarvis products. I further agree
                that any mismatch between my actual financial situation and the
                inputs provided by me may result in an inappropriate risk
                profile assessment, for which I take full responsibility.
              </Text>

              <Text
                variant="caption1"
                sx={{
                  cursor: "pointer",
                  color: "#2B70C5",
                  display: "block",
                  mt: 1,
                }}
                onClick={onToggleReadMore}
              >
                {readMore ? "Read less" : "Read more"}
              </Text>
            </Box>
          </Stack>
        </Stack>

        {/* RIGHT SECTION */}
        <Stack sx={{ flex: 1, minWidth: 0 }} pl={{ xs: 0, md: 4 }}>
          <Text sx={{ fontWeight: 600, mb: 1, fontSize: { xs: "16px", sm: "16px", md: "18px" } }}>PRODUCT FEATURES</Text>

          <Box sx={{ display: "flex", gap: 2, alignItems: "center" , paddingBlock:{xs: "14px", sm: "18px", md: "24px" }}}>
            <ShieldIcon color="primary" />
         
            <Text fontWeight={600} sx={{
           fontSize:  { xs: "13px", sm: "15px", md: "18px" }}}>{productName}</Text>

            <Text
              sx={{
                px: "10px",
                py: "4px",
                fontSize: { xs: "10px", sm: "14px", md: "16px" },
                borderRadius: "21px",
                background: "#d6f7ed",
                color: "#117c65",
              }}
            >
              {suitableText}
            </Text>
          </Box>

          {/* FEATURES */}

          <Stack gap={2} mt={2}>
            {riskData?.map((items, indx) => (
              <FeatureCard
                key={indx}
                title={items.title}
                desc={items.description}
              />
            ))}
          </Stack>

          {/* RIGHT DECLARATION */}
        </Stack>
      </Stack>

      {/* FOOTER BUTTONS */}
      <Stack
        direction={{ xs: "column-reverse", md: "row" }}
        justifyContent="flex-end"
        alignItems="center"
        gap={2}
        pt={1}
        borderTop="1px solid #eeeeee"
      >
        <JarvisButton
          variant="text"
          sx={{
            color: "#2B70C5",
            border: "1px solid #DEDEDE",
            backgroundColor: "white",
            px: 4,
            py: 1.5,
          }}
          onClick={onChangeProfile}
        >
          Change my risk profile manually
        </JarvisButton>

        <JarvisButton
          sx={{ px: 5, py: 1.5 }}
          onClick={onConfirm}
          loading={isLoading}
        >
          I Confirm my risk profile
        </JarvisButton>
      </Stack>
    </Box>
  );
}

export default Assessment;
